/**
 * Number and date formatters for the operator pages: Dashboard, Tenants,
 * Tenant Detail, Pledges, Pledge Detail and FI Tasks.
 *
 * Each of those pages carried its own formatter, and the same quantity came
 * out five ways: 81250 read "$81.3K" on the Dashboard and "$81K" on Tenants,
 * LTV printed through Intl percent while haircut used toFixed(1), and dates
 * appeared as "9/27/2026, 4:10:06 AM", "June 18, 2026", "Sep 11, 2026",
 * "Sep 11, 2026, 04:21 PM" and "Sep 11, 2026, 4:21 PM" with no time zone on
 * any of them. The pages now call these.
 *
 * An absent value (null, undefined, a non-finite number, an empty string)
 * prints {@link ABSENT}. None of these helpers substitutes a zero for a value
 * that was not delivered (.claude/rules/no-fabricated-values.md); a caller
 * that needs to say WHY a value is absent passes a caption beside it.
 */

const LOCALE = 'en-US';

/** What every formatter prints for a value that was not delivered. */
export const ABSENT = '--';

const COUNT = new Intl.NumberFormat(LOCALE);

const MONEY = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'USD',
});

const MONEY_COMPACT = new Intl.NumberFormat(LOCALE, {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  // Both bounds are explicit: with only the maximum set, engines differ on
  // the currency default minimum (Node prints "$125.0K", Chrome "$125K").
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const PERCENT = new Intl.NumberFormat(LOCALE, {
  style: 'percent',
  maximumFractionDigits: 1,
});

function isMeasured(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** A whole-number count, grouped: 1240 -> "1,240". */
export function formatCount(value: number | null | undefined): string {
  return isMeasured(value) ? COUNT.format(value) : ABSENT;
}

const QUANTITY = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 8,
});

/**
 * A pledged unit count: 500 -> "500", 0.25 -> "0.25", 1234.5 -> "1,234.5".
 * Up to eight decimals, because a pledged position can be a fraction of a
 * share or of a crypto unit and the count is the record of WHAT is pledged.
 */
export function formatQuantity(value: number | null | undefined): string {
  return isMeasured(value) ? QUANTITY.format(value) : ABSENT;
}

/** A USD amount in full: 81250 -> "$81,250.00". */
export function formatMoney(value: number | null | undefined): string {
  return isMeasured(value) ? MONEY.format(value) : ABSENT;
}

/** A USD amount in compact form for metric tiles: 81250 -> "$81.3K". */
export function formatMoneyCompact(value: number | null | undefined): string {
  return isMeasured(value) ? MONEY_COMPACT.format(value) : ABSENT;
}

/**
 * A ratio on the 0 to 1 scale printed as a percent: 0.65 -> "65%",
 * 0.125 -> "12.5%". For ltvRatio and haircutPercent on a pledged account.
 */
export function formatRatioAsPercent(value: number | null | undefined): string {
  return isMeasured(value) ? PERCENT.format(value) : ABSENT;
}

/**
 * A value the API already delivers as a percentage, printed as one:
 * 98.5 -> "98.5%". For webhookSuccessRate24h and the avgLtv fields, which the
 * spec describes as percentages (platform-admin.openapi.yaml). Multiplying
 * these by 100 again is the defect this helper replaced (98.5 -> "9850.0%").
 */
export function formatPercentValue(value: number | null | undefined): string {
  return isMeasured(value) ? PERCENT.format(value / 100) : ABSENT;
}

export interface DateFormatOptions {
  /**
   * IANA zone to render in. Defaults to the viewer's zone. Tests pass 'UTC'
   * so the expected string does not depend on the machine running them.
   */
  timeZone?: string;
}

function parse(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** A calendar date: "Sep 11, 2026". */
export function formatDate(
  value: string | null | undefined,
  options: DateFormatOptions = {},
): string {
  const date = parse(value);
  if (!date) return value ? value : ABSENT;
  return date.toLocaleDateString(LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: options.timeZone,
  });
}

/**
 * A timestamp with its zone named: "Sep 11, 2026, 4:21 PM PDT". A timestamp
 * without a zone cannot be compared against a server log or another
 * operator's screen.
 */
export function formatDateTime(
  value: string | null | undefined,
  options: DateFormatOptions = {},
): string {
  const date = parse(value);
  if (!date) return value ? value : ABSENT;
  return date.toLocaleString(LOCALE, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: options.timeZone,
  });
}
